import { useEffect, useState } from "react";
import ContentForm from "../../../../Components/ContentForm";
import TextField from "@material-ui/core/TextField";
import Grid from "@material-ui/core/Grid";
import Button from "@material-ui/core/Button";
import CircularProgress from "@material-ui/core/CircularProgress";
import FormControl from "@material-ui/core/FormControl";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import FormHelperText from "@material-ui/core/FormHelperText";
import Switch from "@material-ui/core/Switch";
import Autocomplete from "@material-ui/lab/Autocomplete";

import { useCidadesSearch } from "../../../../services/api-tansquery/get-list-cities";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import { useDebouncedValue } from "../../../../hooks/useDebounce";
import { createAttendanceLocalSchema, CreateLocalSchemaData } from "./schema";
import { AttendanceLocalCreateData, useAttendanceLocalCreate } from "../../../../services/api-tansquery/attendance-local/create";
import { maskCep, removeMaskCep } from "../../../../utils/maskCep";
import { useHistory } from "react-router-dom";
import { useBreadcrumbs } from "../../../../contexts/breadcrumb";
import Domain from "@material-ui/icons/Domain";
import OrganField from "../components/organ-field";

export default function NewAttendanceLocals() {
    const history = useHistory();
    const { setBreadcrumbs } = useBreadcrumbs();
    const [open, setOpen] = useState(false);
    const [city, setCity] = useState<string | undefined>(undefined);

    const { control, handleSubmit } = useForm<CreateLocalSchemaData>({
        resolver: zodResolver(createAttendanceLocalSchema),
        defaultValues: {
            nome: "",
            slug_unidade: "",
            cep: "",
            endereco: "",
            bairro: "",
            cidade: null,
            complemento: "",
            source: "",
            orgao_id: null,
            ativo: false,
        }
    });

    const debouncedCity = useDebouncedValue(city, 500);

    const { cityList, isFetching } = useCidadesSearch({
        cidade: debouncedCity, estado: "Mato Grosso do Sul"
    });

    const { mutate: handleCreateAttendanceFn, isLoading } =
        useAttendanceLocalCreate();

    const onSubmit = (data: CreateLocalSchemaData) => {
        const { cidade, ...rest } = data;

        const payload = {
            ...rest,
            cep: removeMaskCep(data.cep),
            cidade_id: cidade?.slug,
        };

        handleCreateAttendanceFn(payload as AttendanceLocalCreateData);
    };

    useEffect(() => {
        setBreadcrumbs((prev) => {
            const homeBreadcrumb = prev.find((_, index) => index === 0);
            return [
                homeBreadcrumb,
                {
                    label: "Locais de Atendimento",
                    path: "/locais-de-atendimento",
                    icon: Domain,
                },
                {
                    label: "Gerenciar",
                    path: "",
                },
            ];
        });
    }, [setBreadcrumbs]);

    return (
        <ContentForm label="Gerenciar Local de Atendimento">
            <form onSubmit={handleSubmit(onSubmit)}>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                        <Controller
                            name="nome"
                            control={control}
                            rules={{ required: "Nome é obrigatório" }}
                            render={({ field, fieldState }) => (
                                <TextField
                                    {...field}
                                    required
                                    label="Nome"
                                    variant="outlined"
                                    fullWidth
                                    error={!!fieldState.error}
                                    helperText={
                                        fieldState.error?.message ||
                                        "Informe o nome do local de atendimento"
                                    }
                                    InputProps={{
                                        required: false,
                                    }}
                                />
                            )}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Controller
                            name="slug_unidade"
                            control={control}
                            render={({ field, fieldState }) => (
                                <TextField
                                    {...field}
                                    label="Identificador Externo"
                                    variant="outlined"
                                    fullWidth
                                    error={!!fieldState.error}
                                    helperText={
                                        fieldState.error?.message ||
                                        "Informe o identificador externo (pode ser usado para integrações)"
                                    }
                                />
                            )}
                        />
                    </Grid>
                    <Grid item xs={12} md={12}>
                        <Controller
                            name="cep"
                            control={control}
                            render={({ field, fieldState }) => (
                                <TextField
                                    {...field}
                                    value={maskCep(field?.value)}
                                    label="CEP"
                                    variant="outlined"
                                    required
                                    error={!!fieldState.error}
                                    helperText={
                                        fieldState.error?.message ||
                                        "Informe o CEP do local de atendimento"
                                    }
                                    InputProps={{
                                        required: false,
                                    }}
                                />
                            )}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Controller
                            name="endereco"
                            control={control}
                            render={({ field, fieldState }) => (
                                <TextField
                                    {...field}
                                    label="Endereço"
                                    variant="outlined"
                                    fullWidth
                                    required
                                    error={!!fieldState.error}
                                    helperText={fieldState.error?.message}
                                    InputProps={{
                                        required: false,
                                    }}
                                />
                            )}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Controller
                            name="bairro"
                            control={control}
                            render={({ field, fieldState }) => (
                                <TextField
                                    {...field}
                                    label="Bairro"
                                    variant="outlined"
                                    fullWidth
                                    required
                                    error={!!fieldState.error}
                                    helperText={fieldState.error?.message}
                                    InputProps={{
                                        required: false,
                                    }}
                                />
                            )}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <FormControl required variant="outlined" fullWidth>
                            <Controller
                                name="cidade"
                                control={control}
                                defaultValue={null}
                                render={({ field }) => (
                                    <Autocomplete
                                        {...field}
                                        loadingText="Carregando..."
                                        open={open}
                                        onOpen={() => setOpen(true)}
                                        onClose={() => {
                                            setOpen(false);
                                            setCity(null);
                                        }}
                                        options={cityList}
                                        loading={isFetching}
                                        getOptionLabel={(option) => `${option?.cidade} - ${option?.estado}` || ""}
                                        onChange={(_, value) => field.onChange(value)}
                                        onInputChange={(_, value, reason) => {
                                            if (reason === "input") {
                                                setCity(value);
                                            }
                                        }}
                                        noOptionsText="Sem resultados"
                                        renderInput={(params) => (
                                            <TextField
                                                required
                                                {...params}
                                                label="Cidade"
                                                variant="outlined"
                                                InputProps={{
                                                    ...params.InputProps,
                                                    required: false,
                                                    endAdornment: (
                                                        <>
                                                            {isFetching ? (
                                                                <CircularProgress color="inherit" size={20} />
                                                            ) : null}
                                                            {params.InputProps.endAdornment}
                                                        </>
                                                    ),
                                                }}
                                            />
                                        )}
                                    />
                                )}
                            />
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Controller
                            name="complemento"
                            control={control}
                            render={({ field, fieldState }) => (
                                <TextField
                                    {...field}
                                    label="Complemento"
                                    variant="outlined"
                                    fullWidth
                                    error={!!fieldState.error}
                                    helperText={fieldState.error?.message}
                                />
                            )}
                        />
                    </Grid>
                    <Grid item xs={12} md={12}>
                        <Controller
                            name="source"
                            control={control}
                            render={({ field, fieldState }) => (
                                <TextField
                                    {...field}
                                    label="Mapa (source)"
                                    variant="outlined"
                                    fullWidth
                                    required
                                    error={!!fieldState.error}
                                    helperText={
                                        fieldState.error?.message ||
                                        "Informe a URL do source do mapa"
                                    }
                                    InputProps={{
                                        required: false,
                                    }}
                                />
                            )}
                        />
                    </Grid>
                    <Grid item xs={12} md={12}>
                        <FormControl variant="outlined" fullWidth>
                            <Controller
                                name="orgao_id"
                                control={control}
                                render={({ field, fieldState }) => (
                                    <OrganField
                                        field={field}
                                        fieldState={fieldState}
                                    />
                                )}
                            />
                        </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                        <Controller
                            name="ativo"
                            control={control}
                            render={({ field }) => (
                                <FormControlLabel
                                    control={<Switch {...field} checked={field.value} />}
                                    label="Este local está ativo?"
                                />
                            )}
                        />
                        <FormHelperText>
                            Ative esta opção para permitir que este local seja exibido como
                            disponível para agendamentos no sistema.
                        </FormHelperText>
                    </Grid>
                    <Grid
                        item
                        xs={12}
                        style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}
                    >
                        <Button
                            onClick={() => history.goBack()}
                            variant="text"
                        >
                            Cancelar
                        </Button>
                        <Button
                            disabled={isLoading}
                            type="submit"
                            variant="contained"
                            color="primary"
                        >
                            {!isLoading ? "Salvar" : <CircularProgress />}
                        </Button>
                    </Grid>
                </Grid>
            </form>
        </ContentForm>
    );
}
